import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { TagMark } from './TagMark';
import { Wordmark } from './Wordmark';

export interface LockupProps {
  size?: number;
  dark?: boolean;
  ariaHidden?: boolean;
}

/**
 * Lockup — logo + wordmark composition (TagMark + Wordmark side-by-side).
 *
 * Composition per CONTEXT D-16 + site-assets.jsx line 51 (BRP-01, D-14..D-17).
 * Inner TagMark + Wordmark are rendered with ariaHidden so the Lockup's own title
 * is the only accessible name exposed to screen readers.
 *
 * Default size=48: TagMark at size=48, Wordmark at size=Math.round(48*56/48)=56.
 * Gap 28px matches design source.
 */
export function Lockup({ size = 48, dark = false, ariaHidden = false }: LockupProps): JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();

  const a11yProps = ariaHidden
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-labelledby': titleId } as const);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 28 }} {...a11yProps}>
      {!ariaHidden && (
        <span id={titleId} className="sr-only">
          {t('brand.lockup.title')}
        </span>
      )}
      <TagMark size={size} dark={dark} ariaHidden />
      <Wordmark size={Math.round((size * 56) / 48)} dark={dark} ariaHidden />
    </div>
  );
}

export default Lockup;
